import { ImagePaths } from '../../lib/imageUrls.js';

export const smbCybersecurityAwareness = {

    id: 16,
    slug: 'smb-cybersecurity-awareness',
    ogImage: '/og/courses/smb-cybersecurity-awareness.jpg',
    courseCode: 'SMB-CYBER-AWARE-001',
    title: 'SMB Cybersecurity Awareness Training',
    subtitle: 'Essential security awareness for all employees',
    valueProposition: 'Transform your employees into your strongest security defence with engaging, practical training on identifying threats, protecting data, and maintaining secure behaviours at work.',
    duration: '4 hours',
    students: 'New',
    level: 'Foundational',
    image: ImagePaths.COURSE_1,
    shortDescription: 'SMB-CYBER-AWARE-001 — Security awareness training covering phishing, passwords, MFA, and safe behaviour for all Australian SMB employees.',
    price: {
      aud: 499,
      currency: 'AUD',
      earlyBird: 399
    },
    stripeConfig: {
    priceId: 'price_1T9bE8Ku1nyc322UiNkkn7ed',
    productId: 'prod_U7qv54piX2vp63'
  },
    
    snapshot: {
      whoItsFor: [
        'All SMB employees across all roles',
        'Administrative and office staff',
        'Remote and hybrid workers',
        'New employees requiring security onboarding',
        'Teams needing annual security refresher training'
      ],
      accreditation: 'Aligned to Essential Eight and ACSC guidance on building security-aware cultures'
    },
    
    logistics: {
      classSize: 'Scalable for teams of 5-50 participants',
      requirements: [
        'No technical background required',
        'Computer, tablet, or smartphone with internet access',
        'Reliable internet connection for interactive content',
        'Commitment to applying security practices daily'
      ]
    },
    
    overview: 'This engaging 4-hour course equips all SMB employees with essential cybersecurity awareness to protect themselves and the business from common threats. Through practical scenarios, real-world examples, and interactive exercises, employees learn to identify phishing attempts, create strong passwords, use multi-factor authentication, browse safely, and report suspicious activities. The course emphasises everyday security behaviours that make a real difference—how to spot suspicious emails, protect sensitive information, work securely from home, and handle customer data responsibly. Perfect for onboarding new staff or providing annual security refresher training for existing teams.',
    
    outcomes: [
      'Identify phishing emails, SMS, and social engineering attempts',
      'Create and manage strong, unique passwords and use password managers effectively',
      'Enable and use multi-factor authentication on work accounts',
      'Browse the internet safely and recognise malicious websites',
      'Handle sensitive business and customer data securely',
      'Work securely from home and remote locations',
      'Recognise and report suspicious activities and potential security incidents',
      'Apply security best practices in daily work activities'
    ],
    
    audience: [
      'All SMB employees across technical and non-technical roles',
      'Administrative assistants and office managers',
      'Sales, marketing, and customer service staff',
      'Remote and hybrid workers',
      'New employees requiring security onboarding',
      'Contractors and temporary staff with system access',
      'Leadership and management teams'
    ],
    
    prerequisites: [
      'No cybersecurity knowledge required',
      'Basic computer and internet skills',
      'Access to work accounts for practice exercises'
    ],
    
    modules: [
      {
        title: 'Understanding Cyber Threats in the Workplace',
        duration: '0.75 hours',
        topics: [
          'Why employees are targeted by cybercriminals',
          'Common threats: phishing, malware, ransomware, social engineering',
          'Real Australian SMB breach stories',
          'The human element in cybersecurity',
          'Your role in protecting the business',
          'Security policies and acceptable use guidelines'
        ]
      },
      {
        title: 'Spotting and Avoiding Phishing',
        duration: '1 hour',
        topics: [
          'What is phishing and how does it work?',
          'Red flags in suspicious emails and messages',
          'Phishing via email, SMS (smishing), and phone (vishing)',
          'Interactive phishing identification exercises',
          'What to do if you receive a suspicious message',
          'What to do if you click a malicious link',
          'Reporting phishing to your IT team and authorities'
        ]
      },
      {
        title: 'Password Security and Multi-Factor Authentication',
        duration: '0.75 hours',
        topics: [
          'Creating strong, unique passwords',
          'Why password reuse is dangerous',
          'Using password managers securely',
          'Setting up multi-factor authentication (MFA)',
          'Managing MFA across multiple devices',
          'Keeping authentication methods secure',
          'Password sharing and team account management'
        ]
      },
      {
        title: 'Safe Browsing and Device Security',
        duration: '0.75 hours',
        topics: [
          'Recognising unsafe websites and downloads',
          'Using public Wi-Fi safely',
          'Keeping devices and software updated',
          'Physical device security (laptops, phones, USB drives)',
          'Mobile device security best practices',
          'Secure file sharing and cloud storage',
          'Avoiding malicious software and fake antivirus warnings'
        ]
      },
      {
        title: 'Protecting Sensitive Information',
        duration: '0.5 hours',
        topics: [
          'Understanding what information is sensitive',
          'Handling customer data and personal information',
          'Australian Privacy Principles (APPs) basics for employees',
          'Secure communication: email, chat, phone',
          'Clean desk and screen policies',
          'Disposing of sensitive information safely',
          'Social media and oversharing risks'
        ]
      },
      {
        title: 'Reporting and Responding to Security Concerns',
        duration: '0.25 hours',
        topics: [
          'How to report security concerns and suspicious activity',
          'Who to contact in your organisation',
          'What happens when you report an incident',
          'It\'s okay to make mistakes—report early!',
          'Building a culture of security awareness',
          'Annual refresher training and staying updated'
        ]
      }
    ],
    
    format: {
      type: 'Live webinar or self-paced online modules',
      includes: [
        '4 hours of engaging, interactive content',
        'Phishing identification exercises and quizzes',
        'Real-world scenario-based learning',
        'Password and MFA setup walkthroughs',
        'Quick reference guides and cheat sheets',
        'Security awareness posters for the workplace',
        'Certificate of completion for compliance records',
        'Access to materials for 12 months for refresher training',
        'Annual updates reflecting new threats and tactics'
      ]
    },
    
    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Live webinars monthly, or immediate self-paced access for teams. Group bookings available.'
    },
    
    faqs: [
      {
        question: 'Can we enrol our entire team?',
        answer: 'Yes! For groups of 5 or more, contact us for a private quote. Many SMBs enrol all employees to build a security-aware culture. Each participant receives a certificate of completion for your compliance records.'
      },
      {
        question: 'How long does the course take to complete?',
        answer: 'The course is 4 hours total and can be completed in a single session or broken into shorter modules over multiple sessions. Self-paced access allows employees to learn at their own pace over days or weeks. Most complete it within 1-2 weeks.'
      },
      {
        question: 'Is this suitable for non-technical employees?',
        answer: 'Absolutely—this course is designed for all employees regardless of technical background. We use plain language, real-world examples, and interactive exercises. No cybersecurity or IT knowledge is required.'
      },
      {
        question: 'Do we get certificates for compliance purposes?',
        answer: 'Yes, each employee receives a Certificate of Completion that can be used for compliance records, professional development, and demonstrating security awareness training to clients, auditors, or insurers. Organisation administrators receive a completion report for all enrolled employees.'
      },
      {
        question: 'Can we customise the course with our policies and branding?',
        answer: 'For team enrolments of 20+, we can incorporate your specific security policies, acceptable use guidelines, and branding. This ensures employees see content tailored to your organisation\'s specific tools and requirements. Contact us to discuss customisation.'
      },
      {
        question: 'How often should employees retake this training?',
        answer: 'We recommend annual security awareness training for all employees. The course includes 12 months of access to updated materials reflecting new threats. Many organisations schedule annual refreshers, plus onboarding for new hires.'
      },
      {
        question: 'Will this satisfy cyber insurance requirements?',
        answer: 'Many cyber insurance policies require regular employee security awareness training. This course meets those requirements and provides certificates for proof of compliance. Check your specific policy or contact us to discuss your insurer\'s requirements.'
      },
      {
        question: 'What happens if an employee fails the quizzes?',
        answer: 'Employees can retake quizzes and modules as needed. The goal is learning and building awareness, not testing. Most complete successfully with the interactive content and practical exercises. Managers receive reports showing completion status.'
      }
    ]
};
